import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, DialogTitle, DialogContent, DialogActions, makeStyles } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'

const useStyles = makeStyles(() => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--gap-md, 16px)',
    minWidth: '420px'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  label: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold, 700)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingLeft: '2px'
  },
  input: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    fontFamily: 'var(--font-mono, monospace)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    transition: 'border-color var(--transition-duration-short, 0.15s) ease, box-shadow var(--transition-duration-short, 0.15s) ease',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  select: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    cursor: 'pointer',
    transition: 'border-color var(--transition-duration-short, 0.15s) ease, box-shadow var(--transition-duration-short, 0.15s) ease',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  row: {
    display: 'flex',
    gap: 'var(--gap-md, 12px)'
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '4px 0'
  },
  toggle: {
    position: 'relative',
    width: '36px',
    height: '20px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    outline: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '2px',
      left: '2px',
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      backgroundColor: 'white',
      transition: 'transform 0.2s ease',
      boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
    }
  },
  toggleOn: {
    backgroundColor: 'var(--color-success-500, #22c55e)',
    '&::after': {
      transform: 'translateX(16px)'
    }
  },
  toggleOff: {
    backgroundColor: 'var(--color-neutral-300)',
    '&::after': {
      transform: 'translateX(0)'
    }
  },
  toggleLabel: {
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-700)',
    cursor: 'pointer',
    userSelect: 'none'
  },
  dialogTitle: {
    fontSize: 'var(--text-size-md, 16px)',
    fontWeight: 600,
    paddingBottom: '8px'
  },
  actions: {
    padding: '12px 24px 16px',
    display: 'flex',
    gap: 'var(--gap-sm, 8px)',
    justifyContent: 'flex-end'
  }
}))

const EMPTY_FORM = {
  policyType: 'fetch',
  service: '',
  pathPattern: '',
  method: 'POST',
  mode: 'read',
  enabled: true
}

function PolicyFormDialog ({ open, onClose, onSubmit, editingPolicy, saving }) {
  const classes = useStyles()
  const [form, setForm] = useState(EMPTY_FORM)

  useEffect(() => {
    if (open) {
      if (editingPolicy) {
        let stmt = editingPolicy.policyStatement || {}
        if (typeof stmt === 'string') {
          try { stmt = JSON.parse(stmt) } catch { stmt = {} }
        }
        setForm({
          policyType: editingPolicy.policyType || 'fetch',
          service: stmt.service || '',
          pathPattern: stmt.pathPattern || '',
          method: stmt.method || 'POST',
          mode: stmt.mode || 'read',
          enabled: stmt.enabled !== false
        })
      } else {
        setForm(EMPTY_FORM)
      }
    }
  }, [open, editingPolicy])

  const handleChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleSubmit = useCallback((e) => {
    e.preventDefault()
    if (!form.service || !form.pathPattern || saving) return

    onSubmit({
      programPolicyId: editingPolicy?.programPolicyId,
      programType: 'smart_endpoint',
      policyType: form.policyType,
      policyStatement: {
        service: form.service,
        pathPattern: form.pathPattern,
        method: form.method,
        mode: form.mode,
        enabled: form.enabled
      }
    })
  }, [form, editingPolicy, onSubmit, saving])

  const isEditing = !!editingPolicy
  const canSubmit = form.service && form.pathPattern && !saving

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth='sm'>
      <DialogTitle className={classes.dialogTitle}>
        {isEditing ? 'Edit Policy' : 'Add Policy'}
      </DialogTitle>
      <DialogContent>
        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.row}>
            <div className={classes.field} style={{ flex: 1 }}>
              <label className={classes.label}>Service</label>
              <input
                className={classes.input}
                value={form.service}
                onChange={(e) => handleChange('service', e.target.value)}
                placeholder='core-data'
                required
                autoFocus
                disabled={saving}
              />
            </div>
            <div className={classes.field} style={{ width: '120px' }}>
              <label className={classes.label}>Method</label>
              <select
                className={classes.select}
                value={form.method}
                onChange={(e) => handleChange('method', e.target.value)}
                disabled={saving}
              >
                <option value='GET'>GET</option>
                <option value='POST'>POST</option>
                <option value='PUT'>PUT</option>
                <option value='DELETE'>DELETE</option>
                <option value='PATCH'>PATCH</option>
              </select>
            </div>
          </div>

          <div className={classes.field}>
            <label className={classes.label}>Path Pattern</label>
            <input
              className={classes.input}
              value={form.pathPattern}
              onChange={(e) => handleChange('pathPattern', e.target.value)}
              placeholder='/query/{name}'
              required
              disabled={saving}
            />
          </div>

          <div className={classes.row}>
            <div className={classes.field} style={{ flex: 1 }}>
              <label className={classes.label}>Mode</label>
              <select
                className={classes.select}
                value={form.mode}
                onChange={(e) => handleChange('mode', e.target.value)}
                disabled={saving}
              >
                <option value='read'>Read</option>
                <option value='write'>Write</option>
              </select>
            </div>
            <div className={classes.field} style={{ flex: 1 }}>
              <label className={classes.label}>Policy Type</label>
              <input
                className={classes.input}
                value={form.policyType}
                onChange={(e) => handleChange('policyType', e.target.value)}
                placeholder='fetch'
                disabled={saving}
                style={{ fontFamily: 'inherit' }}
              />
            </div>
          </div>

          <div className={classes.switchRow}>
            <button
              type='button'
              className={`${classes.toggle} ${form.enabled ? classes.toggleOn : classes.toggleOff}`}
              onClick={() => handleChange('enabled', !form.enabled)}
              disabled={saving}
              role='switch'
              aria-checked={form.enabled}
              aria-label='Toggle enabled'
            />
            <span
              className={classes.toggleLabel}
              onClick={() => !saving && handleChange('enabled', !form.enabled)}
            >
              {form.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <SydButton size='sm' variant='secondary' onClick={onClose} disabled={saving}>Cancel</SydButton>
        <SydButton
          size='sm'
          variant='primary'
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {saving ? 'Saving\u2026' : isEditing ? 'Save Changes' : 'Add Policy'}
        </SydButton>
      </DialogActions>
    </Dialog>
  )
}

PolicyFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editingPolicy: PropTypes.object,
  saving: PropTypes.bool
}

export default PolicyFormDialog
